import { browserIsOffline, isTransportFailure } from "./connection";

/**
 * When a failed read is worth asking again, and how long to wait first.
 *
 * Added 2026-09-19. `app/main.tsx` had `retry: false` on every query since the shell was created on
 * 2026-08-28, so one failed request became an error page for the client. Measured the same day in
 * three full Playwright runs against the local stack: the Supabase gateway answered 502 to single
 * RPC reads (`is_platform_admin`, `current_mfa_requirement`, `admin_client_register`) under load,
 * and each 502 put "Översikten kunde inte hämtas" on screen for a read that the next request would
 * have answered. Staging has the same class of failure in its own history: the tick logged `rpc_504`
 * from 2026-09-09 to 2026-09-13 (`docs/pilot-readiness.md`, the notice dated 2026-09-19).
 *
 * **Only failures the gateway produced are asked again, never an answer the database gave.** Every
 * answer PostgREST writes carries a `code`, either a SQLSTATE or its own `PGRST` code, and every
 * refusal this portal writes is one of those with a `PORTAL_*` message. A refusal is an answer, and
 * asking again only asks for the same refusal. What carries no code is a body the gateway in front
 * of PostgREST wrote, a 502 or a 503, and the PostgREST codes for its connection pool and schema
 * cache (`PGRST000` to `PGRST003`, the last of which is its 504) are the same kind of failure from one
 * layer further in. A transport failure is included, a request that never got an answer, except when
 * the browser says it is offline, which `ConnectionNotice` already tells the person about.
 *
 * `PORTAL_UNEXPECTED` is included because `toPortalFailure` turns every one of those gateway bodies
 * into it before some query functions throw. A genuine unexpected server fault is retried too, which
 * costs the person 1.6 seconds before the same error page, and a read retried is still a read.
 *
 * **Reads only.** This is the queries default and nothing sets it on a mutation: a write the gateway
 * answered 502 may or may not have reached the database, and asking again could do it twice.
 */

const MAX_RETRIES = 2;
const TRANSIENT_POSTGREST_CODES = new Set(["PGRST000", "PGRST001", "PGRST002", "PGRST003"]);

export function isTransientReadFailure(error: unknown): boolean {
  if (browserIsOffline()) {
    return false;
  }

  if (isTransportFailure(error)) {
    return true;
  }

  const candidate = error as { code?: unknown; message?: unknown; cause?: unknown } | null;

  if (!candidate || typeof candidate !== "object") {
    return false;
  }

  // A wrapper that names what could not be read, `PORTAL_IDENTITY_READ_FAILED` in
  // `app/auth/usePortalIdentity.ts`, keeps the answer it wraps as its cause, and the cause decides.
  if (candidate.cause !== undefined && candidate.cause !== null) {
    return isTransientReadFailure(candidate.cause);
  }

  if (candidate.code === "PORTAL_UNEXPECTED") {
    return true;
  }

  if (typeof candidate.message === "string" && /\bPORTAL_[A-Z_]+/.test(candidate.message)) {
    return false;
  }

  if (typeof candidate.code === "string" && candidate.code.length > 0) {
    return TRANSIENT_POSTGREST_CODES.has(candidate.code);
  }

  return typeof candidate.message === "string";
}

export function shouldRetryRead(failureCount: number, error: unknown): boolean {
  return failureCount < MAX_RETRIES && isTransientReadFailure(error);
}

/** 400 ms, then 1200 ms. Short, because a person is looking at a loading state while it runs. */
export function readRetryDelay(attemptIndex: number): number {
  return 400 * 3 ** attemptIndex;
}
